import { invalidate } from '$app/navigation';
import { PUBLIC_SITE_DOMAIN } from '$app/env/public';
import { afterSignup } from '~/lib/auth.remote';
import {
  AuthApiError,
  AuthError,
  type SignInWithPasswordCredentials,
  type SignInWithPasswordlessCredentials,
  type SignUpWithPasswordCredentials,
} from '@supabase/supabase-js';
import {
  type QueryClient,
  createMutation,
  createQuery,
  useQueryClient,
} from '@tanstack/svelte-query';
import { useSupabaseClient } from '~/lib/SupabaseProvider.svelte';
import { getApiUserId } from './utils';
import type { DailyMode, StatsRequest, StreakResponse } from '~/types';
import { getStreakQueryKey } from '~/lib/queries.svelte';
import type Stripe from 'stripe';
import { page } from '$app/state';
import { toast } from 'svelte-sonner';
import { getDailyStats } from '~/store/daily.svelte';

export type FormInputEvent = Event & {
  currentTarget: EventTarget & HTMLInputElement;
};

export const signinMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'signin'],
    mutationFn: async (creds: SignInWithPasswordCredentials) => {
      const response = await supabaseClient.auth.signInWithPassword(creds);
      if (response.error) {
        throw response.error;
      }
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
      await invalidate('supabase:auth');
    },
  }));
};

export const magicLinkMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'magiclink'],
    mutationFn: async (creds: SignInWithPasswordlessCredentials) => {
      const response = await supabaseClient.auth.signInWithOtp(creds);
      if (response.error) {
        throw response.error;
      }
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
      await invalidate('supabase:auth');
    },
  }));
};

export const signupMutation = () => {
  const client = useQueryClient();
  return createMutation<{}, AuthError, SignUpWithPasswordCredentials>(() => ({
    mutationKey: ['auth', 'signup'],
    mutationFn: async (creds: SignUpWithPasswordCredentials) => {
      if (!('email' in creds)) {
        throw new AuthApiError('set email', 400, '');
      }
      const res = await fetch('/api/auth?/signup', {
        method: 'POST',
        body: JSON.stringify(creds),
      });
      if (!res.ok) {
        // TODO
        const { error } = await res.json() as { error: Error };
        throw new AuthApiError(error.message, res.status, '');
      }
      return await res.text();
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
      await invalidate('supabase:auth');
    },
  }));
};

export const resendEmailMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'resend'],
    mutationFn: async (email: string) => {
      await supabaseClient.auth.resend({
        type: 'email_change',
        email,
        options: {
          emailRedirectTo: `${PUBLIC_SITE_DOMAIN}?welcome=true`,
        },
      });
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
    },
  }));
};

export const signoutMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'signout'],
    mutationFn: async () => {
      // TODO
      // await supabaseClient.from('user_device_map')
      //   .delete()
      //   .eq('device_id', getUserId())
      //   .select()
      await supabaseClient.auth.signOut();
    },
    onSuccess: async () => {
      client.removeQueries();
      await invalidate('supabase:auth');
      toast.success('logged out');
    },
  }));
};

export const resetPasswordMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'reset-password'],
    mutationFn: async (email: string) => {
      const res = await supabaseClient.auth.resetPasswordForEmail(email, {
        redirectTo: `${PUBLIC_SITE_DOMAIN}/reset`,
      });
      if (res.error instanceof AuthError) {
        throw res.error;
      }
      return res;
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
    },
  }));
};

export const setPasswordMutation = () => {
  const client = useQueryClient();
  const supabaseClient = useSupabaseClient();
  return createMutation(() => ({
    mutationKey: ['auth', 'set-password'],
    mutationFn: async (password: string) => {
      const res = await supabaseClient.auth.updateUser({ password });
      if (res.error) {
        if (res.error.name === 'AuthSessionMissingError') {
          throw new Error('link has expired, try sending a new email');
        }
        throw res.error;
      }
      return res;
    },
    onSuccess: async () => {
      await client.resetQueries({ queryKey: ['auth'] });
    },
  }));
};

export const getStripeSession = (enabled: boolean) =>
  createQuery(() => ({
  queryKey: ['stripe', 'session'],
  queryFn: async () => {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
      });
    if (!res.ok) {
      throw Error('no session');
    }
    return await res.json() as { client_secret: string };
  },
  enabled,
  staleTime: 0,
    refetchOnMount: true,
}));

export const getSubscriptionQueryKey = () => ['stripe', 'subscription'];

export const getSubscriptionQuery = () =>
  createQuery(() => ({
  queryKey: getSubscriptionQueryKey(),
  queryFn: async () => {
    const res = await fetch('/api/stripe/subscription');
    if (!res.ok) {
      throw new AuthApiError(res.statusText, res.status, '');
    }
    return await res.json() as Stripe.Subscription;
  },
  refetchOnMount: true,
  staleTime: 1000 * 60 * 60 * 24,
    enabled: Boolean(page.data.user),
}));

export const getCustomerPortal = () =>
  createMutation(() => ({
  mutationKey: ['stripe', 'subscription', 'portal'],
  mutationFn: async () => {
      const res = await fetch('/api/stripe/subscription', {
        method: 'POST',
      });
    if (!res.ok) {
      throw Error(res.statusText);
    }
    return await res.json() as Stripe.Response<Stripe.BillingPortal.Session>;
    },
}));

export const getUserQuery = () => {
  const supabaseClient = useSupabaseClient();
  return createQuery(() => ({
    queryKey: ['auth', 'user', 'metadata'],
    queryFn: async () => {
      const res = await supabaseClient
        .from('users')
        .select('*, user_device_map(*), payments(*)')
        .eq('id', getApiUserId(page))
        .maybeSingle();

      if (res.error) {
        throw res.error;
      }
      return res.data;
    },
    enabled: Boolean(page.data.user),
  }));
};

const dailyModes: DailyMode[] = ['5x5', '6x6'];

export const initUserMutation = (queryClient: QueryClient) =>
  createMutation(
  () => ({
    mutationFn: async () => {
        const stats: StatsRequest[] = await Promise.all(
          dailyModes.map(async (mode) => {
        const s = await getDailyStats(mode);
        const wonGames = (s.stars[1] ?? 0) + (s.stars[2] ?? 0) + (s.stars[3] ?? 0) + (s.stars[4] ?? 0);
        const totalGames = wonGames + (s.stars[0] ?? 0);
        return {
          mode,
          currStreak: s.currStreak,
          bestStreak: s.bestStreak,
          totalGames,
              wonGames,
        };
          }),
        );

      const result = await afterSignup(stats);
      if (!result.ok) throw new Error(result.error);
      return result.streaks;
    },
    onSuccess: (streaks) => {
      for (const { mode, ...streak } of streaks) {
          queryClient.setQueryData<StreakResponse>(
            getStreakQueryKey(mode),
            streak,
          );
      }
      },
  }),
    () => queryClient,
);
